import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTutors } from '../services/tutorService';
import { useAuth } from './AuthContext';
import { useCacheInvalidation } from './CacheInvalidationContext';

const TutorContext = createContext();

export const useTutors = () => {
    const context = useContext(TutorContext);
    if (!context)
    {
        throw new Error('useTutors must be used within a TutorProvider');
    }
    return context;
};

export const TutorProvider = ({ children }) => {
    const { center, hasPermission } = useAuth();
    const { registerRefresh } = useCacheInvalidation();
    const [tutors, setTutors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTutors = useCallback(async () => {
        if (!center)
        {
            setLoading(false);
            return;
        }

        // Skip fetch if user doesn't have permission
        if (!hasPermission('tutors:list')) {
            setLoading(false);
            setError(null);
            setTutors([]);
            return;
        }

        try
        {
            setLoading(true);
            setError(null);
            const data = await getTutors();
            setTutors(data);
        } catch (err)
        {
            setError(err);
            setTutors([]);
        } finally
        {
            setLoading(false);
        }
    }, [center, hasPermission]);

    // Fetch tutors when center changes
    useEffect(() => {
        fetchTutors();
    }, [fetchTutors]);

    // Poll for updates every 10 minutes (when page is visible)
    useEffect(() => {
        const pollInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchTutors();
            }
        }, 10 * 60 * 1000); // 10 minutes

        return () => clearInterval(pollInterval);
    }, [fetchTutors]);

    // Refresh function to be called after CRUD operations
    const refreshTutors = useCallback(() => {
        return fetchTutors();
    }, [fetchTutors]);

    // Register refresh function for cache invalidation
    useEffect(() => {
        const cleanup = registerRefresh('tutors', refreshTutors);
        return cleanup;
    }, [registerRefresh, refreshTutors]);

    const value = {
        tutors,
        loading,
        error,
        refreshTutors,
        fetchTutors
    };

    return (
        <TutorContext.Provider value={value}>
            {children}
        </TutorContext.Provider>
    );
};

export default TutorContext;
